import React, { useState } from 'react'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { format } from 'date-fns'
import { Typography, MenuItem, File, SquareCheckbox } from '~/legacy/components'
import { TransparentScrollOverlay } from '~/legacy/components/utilities'
import { BASE_ICON_STYLES } from '~/legacy/components/buttons/ButtonUtils'
import { ActionModal } from '~/legacy/components/modals'
import { getBuildingTertiaryName, getSpaceNameByListing } from '~/legacy/utils'

const useBuildingTitleCardStyles = makeStyles({
  buildingContainer: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#F9F9F9',
    width: '100%',
    height: '80px',
    borderRadius: '4px',
  },
  buildingContent: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    padding: '16px 14px',
  },
  buildingImageContainer: {
    display: 'flex',
    width: '48px',
    height: '48px',
    minWidth: '48px',
    marginRight: '16px',
  },
  buildingAddressContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
  },
})

const BuildingTitleCard = (props) => {
  const classes = useBuildingTitleCardStyles()

  if (!props.building) {
    return <div className={clsx(classes.buildingContainer, props.className)} />
  }

  return (
    <div className={clsx(classes.buildingContainer, props.className)}>
      <div className={classes.buildingContent}>
        <div className={classes.buildingImageContainer}>
          <File
            file={props.building.hero_photo || {}}
            skipUploading
            showAsThumbnail
            useMissingBuildingImage
            missingFileStyleOverrides={{
              backgroundColor: '#E0E0E0',
              color: '#666666',
            }}
          />
        </div>
        <div className={classes.buildingAddressContainer}>
          <Typography>{props.building.address}</Typography>
          <Typography>{getBuildingTertiaryName(props.building)}</Typography>
        </div>
      </div>
    </div>
  )
}

const CopySpacesModalContent = withStyles({
  selectSurveyTitle: {
    marginTop: '32px',
    marginBottom: '16px',
  },
  scrollableContainer: {
    maxHeight: '341px',
  },
  row: {
    height: '66px',
    width: '100%',
    borderTop: '1px solid #E0E0E0',
    display: 'flex',
    alignItems: 'center',
    '&:last-child': {
      borderBottom: '1px solid #E0E0E0',
    },
    flexDirection: 'row',
  },
  rowText: {
    lineHeight: '21px',
  },
  rowUpdatedAt: {
    color: '#666666',
  },
  blackBackground: {
    color: '#111',
  },
})(
  ({
    classes,
    building,
    buildingSurveys = [],
    surveyIdToCopy,
    setSurveyIdToCopy = () => {},
  }) => {
    return (
      <div>
        <BuildingTitleCard building={building} />
        <Typography className={classes.selectSurveyTitle}>
          Select a survey to import spaces from
        </Typography>
        <TransparentScrollOverlay
          paddingPx="0"
          hideScroll
          gradientHeightPx={60}
          bottomPaddingPx={40}
          classes={{ scrollableContainer: classes.scrollableContainer }}
        >
          {buildingSurveys.map((survey) => (
            <div key={survey.id} className={classes.row}>
              <div
                style={{
                  display: 'flex',
                  marginLeft: '12px',
                  marginRight: '12px',
                }}
              >
                <SquareCheckbox
                  className={classes.blackBackground}
                  checked={surveyIdToCopy === survey.id}
                  onClick={() => setSurveyIdToCopy(survey.id)}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  paddingRight: '16px',
                }}
              >
                <Typography variant="boldText" className={classes.rowText}>
                  {survey.name}
                </Typography>
                <Typography variant="textSmall" className={classes.rowText}>
                  {(survey.spaces_in_building || [])
                    .map(
                      (space) => getSpaceNameByListing(space) || 'Availability'
                    )
                    .join(', ')}
                </Typography>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  noWrap
                  variant="textSmall"
                  className={clsx(classes.rowText, classes.rowUpdatedAt)}
                >
                  {`Updated ${format(
                    Date.parse(survey.updated_at),
                    'MM/dd/yy'
                  )}`}
                </Typography>
              </div>
            </div>
          ))}
        </TransparentScrollOverlay>
      </div>
    )
  }
)

const useStyles = makeStyles((theme) => ({
  ...BASE_ICON_STYLES(theme),
  textInputFieldContainer: {
    width: '100%',
    marginTop: '10px',
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

export const AddExistingSpacesToBuildingModal = ({
  surveyBuilding,
  otherBuildingSurveys,
  copySpacesFromSurvey,
  open,
  onClose,
}) => {
  const closeModal = () => {
    onClose()
  }

  const [selectedSurveyId, setSelectedSurveyId] = useState(null)

  const onConfirm = async () => {
    if (!selectedSurveyId) {
      return
    }

    await copySpacesFromSurvey({
      copySpacesFromSurveyId: selectedSurveyId,
    })
    closeModal()
  }

  return (
    <ActionModal
      onClose={closeModal}
      onConfirm={onConfirm}
      ModalComponentProps={{
        open: !!open,
        onClose: closeModal,
      }}
      title="Add Spaces to Building"
      confirmButtonLabel="Add to Building"
    >
      <CopySpacesModalContent
        building={surveyBuilding ? surveyBuilding.building : null}
        buildingSurveys={otherBuildingSurveys}
        surveyIdToCopy={selectedSurveyId}
        setSurveyIdToCopy={setSelectedSurveyId}
      />
    </ActionModal>
  )
}

export const AddExistingSpacesToBuildingMenItem = React.forwardRef(
  ({ otherBuildingSurveys, openModal, ...props }, ref) => {
    const classes = useStyles()

    return [
      <MenuItem
        key="add-existing-building-menu-item"
        ref={ref}
        className={classes.menuItemRoot}
        onClick={openModal}
        disabled={!otherBuildingSurveys || !otherBuildingSurveys.length}
        {...props}
      >
        <Typography className={classes.text}> Add Existing Spaces </Typography>
      </MenuItem>,
    ]
  }
)

export const useAddExistingSpacesToBuildingMenuItem = ({
  surveyBuilding,
  surveyId,
  copySpacesFromSurvey,
  handleMenuClose,
}) => {
  const [open, setOpen] = React.useState(false)

  let otherBuildingSurveys = []
  const existingBuildingHasOtherSurveys = !!(
    surveyBuilding &&
    surveyBuilding.building &&
    surveyBuilding.building.surveys_with_spaces &&
    surveyBuilding.building.surveys_with_spaces.length
  )
  if (existingBuildingHasOtherSurveys) {
    otherBuildingSurveys = surveyBuilding.building.surveys_with_spaces.filter(
      (survey) => survey.id !== surveyId
    )
  }

  const AddExistingSpacesToBuildingMenuItemComponent = (
    <AddExistingSpacesToBuildingMenItem
      otherBuildingSurveys={otherBuildingSurveys}
      openModal={() => setOpen(true)}
    />
  )

  const AddExistingSpacesToBuildingModalComponent = (
    <AddExistingSpacesToBuildingModal
      key="add-to-building-menu-modal"
      surveyBuilding={surveyBuilding}
      otherBuildingSurveys={otherBuildingSurveys}
      copySpacesFromSurvey={copySpacesFromSurvey}
      open={open}
      onClose={() => {
        setOpen(false)
        handleMenuClose()
      }}
    />
  )

  return {
    AddExistingSpacesToBuildingMenuItemComponent,
    AddExistingSpacesToBuildingModalComponent,
  }
}
